import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { marker } from '@colsen1991/ngx-translate-extract-marker';
import { IimMarkRequiredModule } from '@iim-angular/components';
import { HighlightValidationError, NotificationMessageLevel, NotificationService } from '@lib/core';
import { BaseSiteLayoutModule } from '@lib/util/base-site-layout';
import { TranslateModule } from '@ngx-translate/core';
import { finalize } from 'rxjs';
import { UpdatePurchasingGroupCommand } from '../../models/update-purchasing-group-command.model';
import { PurchasingGroupDetail } from '../../models/purchasing-group-detail.model';
import { PurchasingGroupDataService } from '../../services/purchasing-group-data.service';

interface IPurchasingGroupMasterDataFormControl {
    readonly code: FormControl<string>;
    readonly name: FormControl<string>;
    readonly description: FormControl<string>;
}

export class PurchasingGroupMasterDataDialogData {
    constructor(public readonly purchasingGroup: PurchasingGroupDetail) {}
}

export class PurchasingGroupMasterDataDialogResult {
    constructor(public readonly updated: boolean) {}
}

const NAME_MAX = 100;
const DESCRIPTION_MAX = 250;

@Component({
    selector: 'lib-purchasing-group-master-data-dialog',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatDialogModule,
        BaseSiteLayoutModule,
        TranslateModule,
        HighlightValidationError,
        IimMarkRequiredModule,
    ],
    templateUrl: './purchasing-group-master-data-dialog.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PurchasingGroupMasterDataDialogComponent {
    private readonly fb = inject(FormBuilder);
    private readonly dataService = inject(PurchasingGroupDataService);
    private readonly notificationService = inject(NotificationService);
    private readonly dialogRef = inject<
        MatDialogRef<PurchasingGroupMasterDataDialogComponent, PurchasingGroupMasterDataDialogResult>
    >(MatDialogRef);
    private readonly data = inject<PurchasingGroupMasterDataDialogData>(MAT_DIALOG_DATA);

    public readonly heading = marker('Einkäufergruppe bearbeiten');
    public readonly icon = 'fas fa-users';
    public readonly nameMax = NAME_MAX;
    public readonly descriptionMax = DESCRIPTION_MAX;
    public readonly saving = signal(false);

    private readonly initialName = this.data.purchasingGroup.name;
    private readonly initialDescription = this.data.purchasingGroup.description ?? '';

    public readonly form: FormGroup<IPurchasingGroupMasterDataFormControl> = this.fb.nonNullable.group<IPurchasingGroupMasterDataFormControl>({
        code: this.fb.nonNullable.control({ value: this.data.purchasingGroup.code, disabled: true }),
        name: this.fb.nonNullable.control(this.initialName, {
            validators: [Validators.required, Validators.maxLength(NAME_MAX)],
        }),
        description: this.fb.nonNullable.control(this.initialDescription, {
            validators: [Validators.maxLength(DESCRIPTION_MAX)],
        }),
    });

    private readonly nameValue = toSignal(this.form.controls.name.valueChanges, {
        initialValue: this.form.controls.name.value,
    });
    private readonly descriptionValue = toSignal(this.form.controls.description.valueChanges, {
        initialValue: this.form.controls.description.value,
    });
    public readonly nameLength = computed(() => this.nameValue().length);
    public readonly descriptionLength = computed(() => this.descriptionValue().length);

    public save(): void {
        this.form.markAllAsTouched();
        this.form.updateValueAndValidity({ onlySelf: false, emitEvent: false });
        if (this.form.invalid || this.saving()) {
            return;
        }

        if (!this.hasChanges()) {
            this.dialogRef.close(new PurchasingGroupMasterDataDialogResult(false));
            return;
        }

        const value = this.form.getRawValue();
        const name = value.name.trim();
        const description = value.description.trim() || null;
        const command = new UpdatePurchasingGroupCommand(this.data.purchasingGroup.id, name, description);

        this.saving.set(true);
        this.dataService
            .update(command)
            .pipe(finalize(() => this.saving.set(false)))
            .subscribe({
                next: () => {
                    this.notificationService.showNotification(
                        marker('Daten erfolgreich gespeichert.'),
                        NotificationMessageLevel.SUCCESS,
                    );
                    this.dialogRef.close(new PurchasingGroupMasterDataDialogResult(true));
                },
                error: (error) => {
                    const message = this.dataService.resolveErrorMessage(
                        error,
                        marker('Formular konnte nicht gespeichert werden. Bitte Eingaben prüfen.'),
                    );
                    this.notificationService.showNotification(message, NotificationMessageLevel.ERROR);
                },
            });
    }

    public cancel(): void {
        this.dialogRef.close(new PurchasingGroupMasterDataDialogResult(false));
    }

    private hasChanges(): boolean {
        const value = this.form.getRawValue();
        return (
            value.name.trim() !== this.initialName.trim() ||
            value.description.trim() !== this.initialDescription.trim()
        );
    }
}
